import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  CmsLandingPage,
  SignInForm,
  SignInFormConfig,
  SignInFormValue,
} from '@checkout/util-models';
import { AuthFacade } from '@domgen/dgx-fe-authentication';
import { CmsContainer } from '@domgen/dgx-fe-cms';
import { filterNullUndefined } from '@domgen/dgx-fe-common';
import { ComponentStore } from '@ngrx/component-store';
import { Observable, combineLatest, first, map, mergeMap, of, tap } from 'rxjs';
import { SignInFormBuilder } from '../sign-in-form-builder/sign-in-form-builder.service';

export interface ViewModel {
  cmsData: CmsLandingPage;
  form?: FormGroup<SignInForm>;
  userAuthenticating: boolean;
}

export interface ComponentState {
  form?: FormGroup<SignInForm>;
  userAuthenticating: boolean;
}

@Injectable()
export class SignInFormComponentService extends ComponentStore<ComponentState> {
  private readonly formInitialValue$: Observable<SignInFormValue | undefined> =
    of(undefined);

  private readonly formConfig$: Observable<SignInFormConfig> = combineLatest({
    cmsData: this.cmsContainer.content$,
    initialValue: this.formInitialValue$,
  });

  readonly form$: Observable<FormGroup<SignInForm>> = this.select(
    (state: ComponentState) => state.form
  ).pipe(filterNullUndefined());

  private readonly userAuthenticating$: Observable<boolean> = this.select(
    (state: ComponentState) => state.userAuthenticating
  );

  readonly vm$: Observable<ViewModel> = combineLatest({
    cmsData: this.cmsContainer.content$,
    form: this.form$,
    userAuthenticating: this.userAuthenticating$,
  });

  constructor(
    private readonly authFacade: AuthFacade,
    private readonly cmsContainer: CmsContainer<CmsLandingPage>,
    private readonly signInFormBuilder: SignInFormBuilder
  ) {
    super({
      userAuthenticating: false,
    });
  }

  private readonly updateUserAuthenticating = this.updater(
    (state: ComponentState, userAuthenticating: boolean) => ({
      ...state,
      userAuthenticating,
    })
  )(
    combineLatest([
      this.authFacade.authenticating$,
      this.authFacade.authenticated$,
      this.authFacade.userLoaded$,
    ]).pipe(
      map(
        ([authenticating, authenticated, userLoaded]) =>
          authenticating || (authenticated && !userLoaded)
      )
    )
  );

  private readonly updateForm = this.updater(
    (state: ComponentState, form: FormGroup<SignInForm>) => ({
      ...state,
      form,
    })
  );

  readonly buildForm = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      mergeMap(() => this.formConfig$),
      first(),
      map((config: SignInFormConfig) => this.signInFormBuilder.build(config)),
      tap((form) => {
        this.updateForm(form);
      })
    )
  );
}
